import React, { useEffect, useState } from "react";
import { X, Search, Send, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { avatarSrc } from "@/lib/defaults";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

// "Envoyer à" sheet: forward one or several messages to another Yalola user/conversation.
// Self-contained: loads recent conversations + debounced user search internally.
export const ForwardSheet = ({ messages, convId, isMobile, t, onClose, onForwarded }) => {
  const open = messages.length > 0;
  const [convs, setConvs] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [sending, setSending] = useState(null);

  useEffect(() => {
    if (!open) return;
    setQuery(""); setResults([]);
    api.get("/chat/conversations")
      .then(({ data }) => setConvs((data.conversations || []).map((c) => c.other).filter(Boolean)))
      .catch(() => {});
  }, [open, messages]);

  useEffect(() => {
    if (!open) return undefined;
    const q = query.trim();
    if (!q) { setResults([]); return undefined; }
    const tmo = setTimeout(() => {
      api.get(`/chat/search-users?q=${encodeURIComponent(q)}`)
        .then(({ data }) => setResults(data.users || []))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(tmo);
  }, [query, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const forwardTo = async (recipient) => {
    if (!messages.length || sending) return;
    setSending(recipient.id);
    try {
      for (const fm of messages) {
        const { data } = await api.post("/chat/messages", {
          recipient_id: recipient.id,
          content: fm.content || "",
          media_url: fm.media_url || null,
          media_type: fm.media_type || null,
          media_group: Array.isArray(fm.media_group) && fm.media_group.length ? fm.media_group : null,
          forwarded: true,
        });
        if (data?.conversation_id && data.conversation_id === convId && data.message) onForwarded(data.message);
      }
      window.dispatchEvent(new CustomEvent("yalola:chat", { detail: { type: "sent" } }));
      toast.success(`${t("chat.sent_to", "Envoyé à")} ${recipient.name}`);
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Envoi impossible"); }
    finally { setSending(null); }
  };

  const list = query.trim() ? results : convs;
  return (
    <div className="fixed inset-0 z-[97] flex items-end sm:items-center justify-center bg-black/50" data-testid="chat-forward-sheet" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[72vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--yalola-border)" }}>
          <div className="font-bold text-base" style={{ color: "var(--yalola-text)" }}>{t("chat.send_to", "Envoyer à")}</div>
          <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-full hover:bg-[color:var(--yalola-surface-alt)]" style={{ color: "var(--yalola-text-muted)" }} data-testid="chat-forward-close"><X size={18} /></button>
        </div>
        <div className="p-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--yalola-text-muted)" }} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("chat.search_placeholder", "Rechercher un contact…")} className="w-full h-10 rounded-full pl-9 pr-3 text-sm outline-none border" style={{ background: "var(--yalola-surface-alt)", borderColor: "var(--yalola-border)", color: "var(--yalola-text)" }} data-testid="chat-forward-search" autoFocus />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-2 pb-3" style={{ paddingBottom: isMobile ? "calc(0.75rem + var(--sa-bottom))" : undefined }}>
          {list.map((u) => (
            <button key={u.id} onClick={() => forwardTo(u)} disabled={sending === u.id} className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-[color:var(--yalola-surface-alt)] text-left disabled:opacity-60" data-testid={`chat-forward-user-${u.id}`}>
              <Avatar className="h-10 w-10">
                <AvatarImage src={avatarSrc(u)} />
                <AvatarFallback>{u.name?.[0]}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate" style={{ color: "var(--yalola-text)" }}>{u.name}</div>
                {u.city && <div className="text-[11px] truncate" style={{ color: "var(--yalola-text-muted)" }}>{u.city}</div>}
              </div>
              {sending === u.id
                ? <Loader2 size={16} className="animate-spin" style={{ color: "var(--yalola-primary)" }} />
                : <Send size={16} style={{ color: "var(--yalola-primary)" }} />}
            </button>
          ))}
          {query.trim() && results.length === 0 && <div className="text-center text-xs py-8" style={{ color: "var(--yalola-text-muted)" }} data-testid="chat-forward-noresult">{t("chat.no_user_found", "Aucun utilisateur trouvé")}</div>}
          {!query.trim() && convs.length === 0 && <div className="text-center text-xs py-8" style={{ color: "var(--yalola-text-muted)" }}>{t("chat.no_recent_chats", "Aucune conversation récente. Utilise la recherche ci-dessus.")}</div>}
        </div>
      </div>
    </div>
  );
};
